import type { ReviewEvidence } from '@console/types'
import { formatAsOf } from '../format.js'
import { evidenceClassLabel, isSharedEvidence } from './evidence-class.js'
import styles from './EvidenceDisplay.module.css'

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * THE ONE PLACE AN EVIDENCE SENTENCE RENDERS — `LGH-036`, `LGH-037`.
 *
 * Extracted from `ReviewPage.tsx`, which had this rendering written out twice
 * (once for a resolved field, once per candidate) — genuinely the same
 * sentence in both places, now one component instead of two copies that could
 * drift. `LGH-037`'s own "one component, four skins" design starts here:
 * this is presentation over `evidence.kind`, which the platform already
 * classified — nothing here infers or reclassifies anything.
 * ═══════════════════════════════════════════════════════════════════════════
 */

/** `catalog` / `tenantVocabulary` / `sharedSuggestion` — the three arms `read_at` travels on. */
function hasReadAt(
  evidence: ReviewEvidence,
): evidence is Extract<ReviewEvidence, { readAt: string }> {
  return 'readAt' in evidence
}

export function EvidenceLine({
  evidence,
  as: Tag = 'p',
}: {
  evidence: ReviewEvidence
  /**
   * `'span'` when this renders inside a `<button>` (a candidate row) — `<p>`
   * is not valid phrasing content there. Defaults to `<p>`, the standalone
   * case (a resolved field's own evidence line).
   */
  as?: 'p' | 'span'
}) {
  return (
    <Tag className={styles['evidence']}>
      {/*
        `LGH-036`, `SPEC-CPM-001 §8`. The class label reads before the
        sentence — trust weight is the first thing a reader needs, not a
        detail found at the end of a line they've already decided to skim.
      */}
      <span
        className={styles['evidenceClass']}
        data-shared={isSharedEvidence(evidence) ? 'true' : undefined}
      >
        {evidenceClassLabel(evidence)}
      </span>{' '}
      {evidence.says}
      {evidence.kind === 'operatorDecision' && (
        // Amendment 5 made visible: which fields the operator settled, and
        // which the drafter did. "You're in the loop" checked by looking.
        <span className={styles['byOperator']}> — {evidence.operator.name}</span>
      )}
      {/*
        `LGH-018` §1, `R-075`. *"Zoho showed 8 units as of 09:14"* — the read
        is a fact about a moment, not a standing truth.
      */}
      {hasReadAt(evidence) && (
        <span className={styles['readAt']}> — read {formatAsOf(evidence.readAt)}</span>
      )}
    </Tag>
  )
}

/** The one place a `raw_text` span is highlighted, and it degrades rather than errors. */
export function Highlighted({ text, span }: { text: string; span: string }) {
  const at = span === '' ? -1 : text.indexOf(span)
  if (at < 0) return <>{text}</>
  return (
    <>
      {text.slice(0, at)}
      <mark className={styles['span']}>{span}</mark>
      {text.slice(at + span.length)}
    </>
  )
}
